import React from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Box,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { ContactTypes } from "../../models/ContactTypes";

const FILE_FORMAT_OPTIONS = [
  { value: "PDF", label: "PDF (.pdf)" },
  { value: "EXCELOPENXML", label: "Excel (.xlsx)" },
  { value: "EXCEL", label: "Excel Legacy (.xls)" },
  { value: "CSV", label: "CSV (.csv)" },
  { value: "WORDOPENXML", label: "Word (.docx)" },
  { value: "XML", label: "XML (.xml)" },
];

// Helper: comma-separated string → array
const toArray = (val) =>
  val ? val.split(",").map((v) => v.trim()).filter(Boolean) : [];

// Helper: array → comma-separated string
const toString = (arr) => arr.join(",");

function SectionHeading({ title }) {
  return (
    <Box mb={1.5}>
      <Typography variant="subtitle2" color="primary" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Divider />
    </Box>
  );
}

function MultiFormatSelect({ label, name, value, onChange }) {
  // value coming in is a comma-separated string, e.g. "PDF,CSV"
  const selected = toArray(value);

  const handleMultiChange = (e) => {
    const { value: newVal } = e.target;
    // MUI multi-select gives an array; convert back to comma-separated string
    const asString = toString(typeof newVal === "string" ? newVal.split(",") : newVal);
    onChange({ target: { name, value: asString } });
  };

  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        name={name}
        value={selected}
        onChange={handleMultiChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selectedVals) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectedVals.map((val) => {
              const option = FILE_FORMAT_OPTIONS.find((o) => o.value === val);
              return <Chip key={val} label={option?.label ?? val} size="small" />;
            })}
          </Box>
        )}
      >
        {FILE_FORMAT_OPTIONS.map(({ value: optVal, label: optLabel }) => (
          <MenuItem key={optVal} value={optVal}>
            {optLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function ReportTab({ formData, handleChange }) {
  return (
    <Box display="flex" flexDirection="column" gap={3}>

      {/* ── Address Section ──────────────────────────────────────────── */}
      <Box>
        <SectionHeading title="Address" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Reporting Name" name="reportingName"
              value={formData.reportingName || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Address" name="reportAddress"
              value={formData.reportAddress || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Address 2" name="reportAddress2"
              value={formData.reportAddress2 || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="City" name="reportCity"
              value={formData.reportCity || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField label="State" name="reportState"
              value={formData.reportState || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Zip" name="reportZip"
              value={formData.reportZip || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Country" name="reportCountry"
              value={formData.reportCountry || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3} />
        </Grid>
      </Box>

      {/* ── Contact Section ──────────────────────────────────────────── */}
      <Box>
        <SectionHeading title="Contact" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Phone 1 Type</InputLabel>
              <Select name="reportingPhone1Type"
                value={formData.reportingPhone1Type || ""}
                onChange={handleChange} label="Phone 1 Type">
                {Object.entries(ContactTypes).map(([key, { text, value }]) => (
                  <MenuItem key={key} value={value}>{text}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Phone 1" name="reportPhone1"
              value={formData.reportPhone1 || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Phone 2 Type</InputLabel>
              <Select name="reportingPhone2Type"
                value={formData.reportingPhone2Type || ""}
                onChange={handleChange} label="Phone 2 Type">
                {Object.entries(ContactTypes).map(([key, { text, value }]) => (
                  <MenuItem key={key} value={value}>{text}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Phone 2" name="reportPhone2"
              value={formData.reportPhone2 || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Contact Person" name="reportContact"
              value={formData.reportContact || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Email" name="reportEmail"
              value={formData.reportEmail || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Fax" name="reportFax"
              value={formData.reportFax || ""} onChange={handleChange}
              fullWidth size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={3} />
        </Grid>
      </Box>

      {/* ── Report Formats Section ───────────────────────────────────── */}
      <Box>
        <SectionHeading title="Report Formats" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <MultiFormatSelect
              label="Lab Report Format"
              name="labReportFileFormat"
              value={formData.labReportFileFormat || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MultiFormatSelect
              label="Invoice Report Format"
              name="invoiceReportFileFormat"
              value={formData.invoiceReportFileFormat || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} />
          <Grid item xs={12} sm={6} md={3} />
        </Grid>
      </Box>

    </Box>
  );
}